import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { searchGifs, getTrendingGifs, isGiphyConfigured } from '../../api/giphy';
import { filesApi } from '../../api/files';
import '../../css/chats/GifPicker.css';

/**
 * Один элемент GIF в сетке пикера.
 * Отображает превью, при клике начинает загрузку и отправку.
 */
function GifItem({ gif, onSelect, uploading }) {
    const [loaded, setLoaded] = useState(false);

    return (
        <button
            type="button"
            className={`gif-picker__gif ${uploading ? 'gif-picker__gif--uploading' : ''}`}
            onClick={() => !uploading && onSelect(gif)}
            disabled={uploading}
            title={gif.title || 'GIF'}
        >
            <img
                src={gif.previewUrl}
                alt={gif.title || 'GIF'}
                loading="lazy"
                onLoad={() => setLoaded(true)}
                className={loaded ? 'gif-picker__gif-img--loaded' : ''}
            />
            {!loaded && <span className="gif-picker__gif-placeholder">⏳</span>}
            {uploading && <div className="gif-picker__gif-overlay"><span>⬆</span></div>}
        </button>
    );
}

/**
 * Панель выбора GIF.
 * Содержит поисковую строку и сетку результатов.
 * При выборе GIF скачивает его, загружает в files API
 * и вызывает onSelect с fileId для отправки.
 *
 * @param {React.RefObject} anchorRef - Ref на элемент-якорь
 * @param {Function} onSelect - Колбэк при выборе: ({ fileId, url, gifId }) => void
 * @param {Function} onClose - Колбэк при закрытии
 */
export default function GifPicker({ anchorRef, onSelect, onClose }) {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [uploadingId, setUploadingId] = useState(null);
    const [notConfigured, setNotConfigured] = useState(false);
    const [coords, setCoords] = useState(null);
    const containerRef = useRef(null);
    const searchInputRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    const PICKER_WIDTH = 340;
    const GAP = 8;
    const MARGIN = 8;

    // Загрузка трендов при открытии
    useEffect(() => {
        if (!isGiphyConfigured()) {
            setNotConfigured(true);
            setLoading(false);
            return;
        }

        let cancelled = false;
        const loadTrending = async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await getTrendingGifs({ limit: 20 });
                if (!cancelled) setResults(data);
            } catch (err) {
                if (!cancelled) {
                    setError(err.message || 'Не удалось загрузить GIF');
                    console.error('[GifPicker] Trending failed:', err.message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        loadTrending();
        // Фокус на поиск
        setTimeout(() => searchInputRef.current?.focus(), 100);
        return () => { cancelled = true; };
    }, []);

    // Поиск с debounce
    useEffect(() => {
        if (notConfigured) return;

        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        if (!query.trim()) {
            // Пустой запрос — показываем тренды
            setLoading(true);
            setError(null);
            getTrendingGifs({ limit: 20 })
                .then(data => { if (!searchTimeoutRef.current) setResults(data); })
                .catch(() => {})
                .finally(() => setLoading(false));
            return;
        }

        searchTimeoutRef.current = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await searchGifs(query.trim(), { limit: 20 });
                setResults(data);
            } catch (err) {
                setError('Ошибка поиска');
            } finally {
                setLoading(false);
            }
        }, 400);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = null;
            }
        };
    }, [query]);

    // Вычисление позиции (как в StickerPicker)
    const computePosition = useCallback(() => {
        const anchor = anchorRef?.current;
        if (!anchor) return null;
        const rect = anchor.getBoundingClientRect();
        const pickerEl = containerRef.current;
        const pickerHeight = pickerEl ? pickerEl.offsetHeight : 400;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let top;
        const spaceAbove = rect.top;
        const spaceBelow = vh - rect.bottom;

        if (spaceAbove >= pickerHeight + GAP) {
            top = rect.top - pickerHeight - GAP;
        } else if (spaceBelow >= pickerHeight + GAP) {
            top = rect.bottom + GAP;
        } else if (spaceAbove >= spaceBelow) {
            top = MARGIN;
        } else {
            top = Math.max(MARGIN, vh - pickerHeight - MARGIN);
        }

        let left = rect.left;
        if (left + PICKER_WIDTH > vw - MARGIN) {
            left = vw - PICKER_WIDTH - MARGIN;
        }
        if (left < MARGIN) left = MARGIN;

        return { top, left };
    }, [anchorRef]);

    useLayoutEffect(() => {
        setCoords(computePosition());
    }, [computePosition]);

    useLayoutEffect(() => {
        if (coords === null) return;
        const pos = computePosition();
        if (pos) setCoords(pos);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coords === null]);

    // Пересчёт при scroll/resize
    useEffect(() => {
        const handle = () => {
            const pos = computePosition();
            if (pos) setCoords(pos);
        };
        window.addEventListener('scroll', handle, { capture: true, passive: true });
        window.addEventListener('resize', handle);
        return () => {
            window.removeEventListener('scroll', handle, { capture: true, passive: true });
            window.removeEventListener('resize', handle);
        };
    }, [computePosition]);

    // Закрытие по клику вне и Escape
    useEffect(() => {
        if (!onClose) return;
        const handlePointerDown = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                onClose();
            }
        };
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                onClose();
            }
        };
        const timer = setTimeout(() => {
            document.addEventListener('mousedown', handlePointerDown);
            document.addEventListener('touchstart', handlePointerDown);
            document.addEventListener('keydown', handleKeyDown);
        }, 0);
        return () => {
            clearTimeout(timer);
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    /**
     * Обработка выбора GIF.
     * Скачиваем GIF (уменьшенную версию), загружаем в наш files API, вызываем onSelect с fileId.
     */
    const handleSelect = useCallback(async (gif) => {
        if (uploadingId) return;
        setUploadingId(gif.id);
        setError(null);

        try {
            // Используем уменьшенную версию GIF (fixed_width) для экономии трафика
            const downloadUrl = gif.previewUrl || gif.url;
            if (!downloadUrl) throw new Error('Нет URL для скачивания');

            // Скачиваем GIF с Giphy с таймаутом 30 сек
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000);

            const response = await fetch(downloadUrl, { signal: controller.signal });
            clearTimeout(timeoutId);

            if (!response.ok) throw new Error(`Ошибка скачивания: ${response.status}`);
            const blob = await response.blob();

            if (blob.size === 0) throw new Error('Пустой файл');
            if (blob.size > 10 * 1024 * 1024) throw new Error('GIF слишком большой (>10MB)');

            // Создаём File объект
            const file = new File([blob], `gif-${gif.id}.gif`, { type: 'image/gif' });

            // Загружаем в наш files API через универсальный метод upload
            const uploadResult = await filesApi.upload(file, {
                type: 'IMAGE',
                entityType: 'CHAT',
            });
            const fileData = uploadResult?.file || uploadResult;
            const fileId = fileData?.id;

            if (!fileId) throw new Error('Сервер не вернул ID файла');

            // Вызываем колбэк с результатом
            onSelect?.({
                fileId,
                url: gif.url,
                gifId: gif.id,
                title: gif.title,
                previewUrl: gif.previewUrl,
            });
            onClose?.();
        } catch (err) {
            console.error('[GifPicker] Failed to process GIF:', err.message);
            // Показываем конкретную ошибку пользователю
            if (err.name === 'AbortError') {
                setError('Скачивание GIF прервано (слишком долго)');
            } else {
                setError(err.message || 'Не удалось загрузить GIF');
            }
        } finally {
            setUploadingId(null);
        }
    }, [uploadingId, onSelect, onClose]);

    const pickerStyle = coords
        ? { position: 'fixed', top: `${coords.top}px`, left: `${coords.left}px` }
        : { position: 'fixed', top: 0, left: 0, visibility: 'hidden' };

    return createPortal(
        <div
            ref={containerRef}
            className="gif-picker"
            style={pickerStyle}
        >
            {/* Поисковая строка */}
            <div className="gif-picker__search">
                <span className="gif-picker__search-icon">🔍</span>
                <input
                    ref={searchInputRef}
                    type="text"
                    className="gif-picker__search-input"
                    placeholder="Поиск GIF..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') onClose?.();
                    }}
                />
                {query && (
                    <button
                        type="button"
                        className="gif-picker__search-clear"
                        onClick={() => setQuery('')}
                    >
                        ✕
                    </button>
                )}
            </div>

            {/* Результаты */}
            <div className="gif-picker__results">
                {notConfigured && (
                    <div className="gif-picker__not-configured">
                        <div className="gif-picker__not-configured-icon">🔑</div>
                        <div className="gif-picker__not-configured-title">Требуется API-ключ GIPHY</div>
                        <div className="gif-picker__not-configured-text">
                            Чтобы использовать GIF, получите бесплатный API-ключ на
                            {' '}<a href="https://developers.giphy.com/" target="_blank" rel="noopener noreferrer">developers.giphy.com</a>
                            {' '}и добавьте его в файл <code>.env</code>:
                        </div>
                        <div className="gif-picker__not-configured-code">
                            REACT_APP_GIPHY_API_KEY=ваш_ключ
                        </div>
                    </div>
                )}
                {!notConfigured && loading && (
                    <div className="gif-picker__loading">Загрузка GIF…</div>
                )}
                {!notConfigured && error && (
                    <div className="gif-picker__error">{error}</div>
                )}
                {!notConfigured && !loading && !error && results.length === 0 && (
                    <div className="gif-picker__empty">
                        {query.trim() ? 'Ничего не найдено' : 'GIF пока недоступны'}
                    </div>
                )}
                {!notConfigured && !loading && !error && results.length > 0 && (
                    <div className="gif-picker__grid">
                        {results.map(gif => (
                            <GifItem
                                key={gif.id}
                                gif={gif}
                                onSelect={handleSelect}
                                uploading={uploadingId === gif.id}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Подпись Giphy */}
            <div className="gif-picker__attribution">
                <span>Powered by</span>
                <span className="gif-picker__attribution-logo">GIPHY</span>
            </div>
        </div>,
        document.body
    );
}